import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type MailingCampaign,
  type MailingCampaignSend,
  type MailingResendUsage,
  type MailingSequence,
  type MailingSequenceStep,
  type MailingTemplate,
} from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DocMarkdownEditor from "@/components/DocMarkdownEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

type Tab = "contacts" | "templates" | "campaigns" | "sequences" | "settings";

const TABS: { id: Tab; label: string }[] = [
  { id: "contacts", label: "Kontakty" },
  { id: "templates", label: "Szablony" },
  { id: "campaigns", label: "Kampanie" },
  { id: "sequences", label: "Seria maili" },
  { id: "settings", label: "Ustawienia" },
];

const TAB_HELP: Record<
  Tab,
  { title: string; blurb: string; steps: string[]; notes: string[] }
> = {
  contacts: {
    title: "Kontakty",
    blurb: "To Twoja lista mailingowa. Tu są adresy, nie treść listów.",
    steps: [
      "Pasek kafelków: klik filtruje listę (status albo tag). Ponowny klik czyści filtr.",
      "Szukaj po mailu albo imieniu.",
      "Dodaj jedną osobę ręcznie albo wklej CSV (kolumna email).",
      "Import z zamówień bierze kupujących z Wysyłki i oznacza tagiem buyer.",
    ],
    notes: [
      "Tagi (buyer, mailerlite, waitlist…) służą do wyboru odbiorców kampanii i serii.",
      "„Nigdy nie otworzyli” / „Otwierali” to dane z MailerLite przy imporcie, nie z Resend.",
      "Status unsubscribed / bounced / complained: nie wyślemy do nich.",
    ],
  },
  templates: {
    title: "Szablony",
    blurb: "Gotowa treść do ponownego użycia. Nie wysyła się sama.",
    steps: [
      "Nazwa + temat + treść w edytorze (jak w Dokumentach).",
      "Chipami wstaw {{name}}, {{first_name}}, {{email}} (wypełnią się przy wysyłce).",
      "Obraz: ikona krajobrazu, wklejenie albo przeciągnięcie.",
      "Podgląd dokleja stopkę. Zapisz. W kampanii: Wstaw szablon.",
    ],
    notes: [
      "Stopki nie wpisuj w treść szablonu: jest jedna, globalna.",
      "Link wypisu dokleja się sam przy prawdziwej wysyłce.",
    ],
  },
  campaigns: {
    title: "Kampanie",
    blurb: "Jeden list, jeden strzał, do wielu osób naraz.",
    steps: [
      "Napisz draft albo kliknij istniejący draft, żeby edytować.",
      "Opcjonalnie: Wstaw szablon. Chipami dodaj {{name}} / {{email}}.",
      "Zapisz. Wyślij test na swój mail. Potem Wyślij na bazę.",
      "Kliknij kampanię w toku / wysłaną, żeby zobaczyć postęp i listę odbiorców.",
    ],
    notes: [
      "Draft i cancelled da się edytować. Po starcie kolejki tylko śledzenie i Anuluj.",
      "Postęp odświeża się sam. Statusy: pending → sent / failed / bounced…",
    ],
  },
  sequences: {
    title: "Seria maili (sekwencja)",
    blurb: "Nie jeden list, tylko łańcuch: dzień 0, potem za 2 dni, potem za tydzień. Jak autoresponder w MailerLite.",
    steps: [
      "Nadaj nazwę (np. Welcome po zakupie) i utwórz serię.",
      "Krok 1: opóźnienie 0 h = od razu po włączeniu osoby.",
      "Krok 2+: liczba godzin od poprzedniego listu (48 = dwa dni).",
      "Zapisz kroki. Potem wpisz tag i kliknij Włącz kontakty z tagiem.",
    ],
    notes: [
      "Kampania = wszyscy naraz, raz. Seria = każda osoba dostaje kolejne listy według zegara.",
      "Włączenie bierze aktywne kontakty z tym tagiem, które jeszcze nie są w tej serii.",
      "Kolejka leci w tle (co około minutę). Wypisane i bounce nie dostaną następnych.",
    ],
  },
  settings: {
    title: "Ustawienia",
    blurb: "Nadawca, tempo i stopka wspólna dla wszystkich listów.",
    steps: [
      "Sprawdź limity Resend (dzień / miesiąc / domeny) nad zakładkami i tu poniżej.",
      "Stopka: podpis, logo, link do sklepu. Zapisz stopkę.",
      "Rate/min: ile maili na minutę (bezpieczniej 20–40).",
      "Wyślij test na swój adres zanim ruszysz kampanię.",
    ],
    notes: [
      "From i klucz API są z serwera (Resend), nie z tego formularza.",
      "Plan Resend (Pro): bez limitu 100/dzień; miesięczny wg RESEND_PLAN_MONTHLY_LIMIT (domyślnie 50k). Usage API bywa niedostępne (beta).",
      "Public base to adres w linkach obrazków i wypisu. Na prod: kasiazareba.pl.",
    ],
  },
};

function MailingHelp({ tab }: { tab: Tab }) {
  const h = TAB_HELP[tab];
  return (
    <aside className="lg:sticky lg:top-4 self-start space-y-3 rounded-lg border border-amber-200/80 bg-amber-50/70 p-4 text-sm text-slate-800">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/80">Co tu robisz</p>
        <h2 className="mt-1 text-base font-semibold leading-snug">{h.title}</h2>
        <p className="mt-1.5 leading-relaxed text-slate-700">{h.blurb}</p>
      </div>
      <ol className="list-decimal space-y-1.5 pl-4 leading-relaxed">
        {h.steps.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>
      <ul className="space-y-1.5 border-t border-amber-200/80 pt-3 text-xs leading-relaxed text-slate-600">
        {h.notes.map((n) => (
          <li key={n}>· {n}</li>
        ))}
      </ul>
    </aside>
  );
}

function errMsg(e: unknown) {
  return e instanceof Error ? e.message : String(e);
}

async function uploadMailingImage(file: File) {
  return api.mailingUploadAsset(file);
}

function MailingEditor({
  editorKey,
  value,
  onChange,
  minHeightClass = "min-h-[180px]",
}: {
  editorKey: string;
  value: string;
  onChange: (md: string) => void;
  minHeightClass?: string;
}) {
  return (
    <DocMarkdownEditor
      key={editorKey}
      initialMarkdown={value}
      onMarkdownChange={onChange}
      onUploadImage={uploadMailingImage}
      hideVideo
      placeholder="Treść maila… wklej obraz albo wstaw z paska"
      minHeightClass={minHeightClass}
    />
  );
}

const MERGE_CHIPS = [
  { token: "{{name}}", label: "Imię i nazwisko" },
  { token: "{{first_name}}", label: "Imię" },
  { token: "{{email}}", label: "Email" },
] as const;

function MergeChips({
  onInsert,
}: {
  onInsert: (token: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      <span className="text-muted-foreground">Pola:</span>
      {MERGE_CHIPS.map((c) => (
        <button
          key={c.token}
          type="button"
          title={c.label}
          className="rounded-md border border-border bg-muted/40 px-2 py-0.5 font-mono hover:bg-brand/10 hover:border-brand/40"
          onClick={() => onInsert(c.token)}
        >
          {c.token}
        </button>
      ))}
    </div>
  );
}

function ProgressBar({
  progress,
  className,
}: {
  progress?: { total: number; done: number; pct: number } | null;
  className?: string;
}) {
  if (!progress || !progress.total) return null;
  return (
    <div className={cn("space-y-1", className)}>
      <div className="flex justify-between text-[11px] text-muted-foreground">
        <span>
          {progress.done}/{progress.total}
        </span>
        <span>{progress.pct}%</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-brand transition-all"
          style={{ width: `${Math.min(100, progress.pct)}%` }}
        />
      </div>
    </div>
  );
}

function formatQuota(used?: number | null, limit?: number | null): string {
  const u = used == null ? "…" : String(used);
  if (limit == null || limit <= 0) return `${u} / bez limitu`;
  return `${u} / ${limit}`;
}

function quotaWarn(used?: number | null, limit?: number | null): boolean {
  if (used == null || limit == null || limit <= 0) return false;
  return used / limit >= 0.8;
}

function ResendUsageStrip({ detailed = false }: { detailed?: boolean }) {
  const usageQ = useQuery({
    queryKey: ["mailing-resend-usage"],
    queryFn: () => api.mailingResendUsage(),
    refetchInterval: 60_000,
  });
  const u = usageQ.data as MailingResendUsage | undefined;
  const daily = u?.emails?.daily;
  const monthly = u?.emails?.monthly;
  const domains = u?.domains;
  const rate = u?.rate_limit;

  const tiles = [
    {
      key: "daily",
      label: "Resend dziś",
      value: formatQuota(daily?.used, daily?.limit),
      warn: quotaWarn(daily?.used, daily?.limit),
    },
    {
      key: "monthly",
      label: "Resend miesiąc",
      value: formatQuota(monthly?.used, monthly?.limit),
      warn: quotaWarn(monthly?.used, monthly?.limit),
    },
    {
      key: "domains",
      label: "Domeny",
      value:
        domains?.used == null
          ? "…"
          : domains.limit == null
            ? String(domains.used)
            : `${domains.used} / ${domains.limit}`,
      warn: false,
    },
    {
      key: "rate",
      label: "API rate",
      value:
        rate?.limit == null
          ? "…"
          : rate.remaining == null
            ? `${rate.limit}/s`
            : `${rate.remaining}/${rate.limit}`,
      warn: rate?.remaining != null && rate.remaining <= 1,
    },
  ];

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {tiles.map((t) => (
          <div
            key={t.key}
            className={cn(
              "min-w-[7.5rem] rounded-lg border px-3 py-2",
              t.warn
                ? "border-amber-300 bg-amber-50 text-amber-950"
                : "border-border/70 bg-background",
            )}
          >
            <div className="text-[11px] text-muted-foreground leading-tight">{t.label}</div>
            <div className="text-lg font-semibold tabular-nums">{t.value}</div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {u?.usage_api_available
          ? "Źródło: Resend Usage API."
          : "Usage API Resend niedostępne (beta). Licznik z naszych wysyłek + limity planu (Pro: bez limitu dziennego)."}
        {u && !usageQ.isFetching ? "" : usageQ.isLoading ? " Ładowanie…" : ""}
      </p>
      {detailed && u && (
        <div className="rounded-lg border border-border/70 p-3 space-y-2 text-sm">
          <div className="font-medium">Szczegóły Resend</div>
          <ul className="space-y-1 text-muted-foreground text-xs">
            <li>
              Lokalne wysyłki (status sent): dziś {u.local_sends?.today ?? 0}, miesiąc{" "}
              {u.local_sends?.month ?? 0}
            </li>
            <li>
              Dzień źródło: {daily?.source || "-"}
              {daily?.resets_at ? ` · reset ${daily.resets_at}` : ""}
            </li>
            <li>
              Miesiąc źródło: {monthly?.source || "-"}
              {monthly?.resets_at ? ` · reset ${monthly.resets_at}` : ""}
            </li>
            {(domains?.items || []).map((d) => (
              <li key={String(d.id || d.name)}>
                Domena: {d.name || "?"} · {d.status || "?"}
                {d.region ? ` · ${d.region}` : ""}
              </li>
            ))}
            {(u.notes || []).map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function Mailing() {
  const [tab, setTab] = useState<Tab>("contacts");
  const settingsQ = useQuery({
    queryKey: ["mailing-settings"],
    queryFn: () => api.mailingSettings(),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Mailing"
        subtitle="Lista u nas, wysyłka przez Resend. Z prawej: co robić na tej zakładce."
      />
      {settingsQ.data?.test_only && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Tryb testowy: brak pełnego FROM albo domena. Wysyłka tylko na{" "}
          {(settingsQ.data.test_allowlist || []).join(", ") || "RESEND_TEST_ALLOWLIST"}.
        </div>
      )}
      <div className="mb-3">
        <ResendUsageStrip />
      </div>
      <div className="mb-4 flex flex-wrap gap-1 border-b border-border pb-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              tab === t.id
                ? "bg-brand/15 text-brand"
                : "text-muted-foreground hover:bg-muted/60",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_17.5rem]">
        <div className="min-w-0">
          {tab === "contacts" && <ContactsTab />}
          {tab === "templates" && <TemplatesTab />}
          {tab === "campaigns" && <CampaignsTab />}
          {tab === "sequences" && <SequencesTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
        <MailingHelp tab={tab} />
      </div>
    </div>
  );
}

function ContactsTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [csv, setCsv] = useState("");
  const [msg, setMsg] = useState("");

  const statsQ = useQuery({
    queryKey: ["mailing-contacts-stats"],
    queryFn: () => api.mailingContactsStats(),
  });

  const listQ = useQuery({
    queryKey: ["mailing-contacts", q, statusFilter, tagFilter],
    queryFn: () =>
      api.mailingContacts({
        q,
        status: statusFilter || undefined,
        tag: tagFilter || undefined,
        limit: 200,
      }),
  });

  const createM = useMutation({
    mutationFn: () => api.mailingCreateContact({ email, name }),
    onSuccess: () => {
      setEmail("");
      setName("");
      setMsg("Dodano kontakt.");
      qc.invalidateQueries({ queryKey: ["mailing-contacts"] });
      qc.invalidateQueries({ queryKey: ["mailing-contacts-stats"] });
    },
    onError: (e) => setMsg(errMsg(e)),
  });

  const importCsvM = useMutation({
    mutationFn: () => api.mailingImportCsv(csv),
    onSuccess: (r) => {
      setMsg(`CSV: +${r.created} / upd ${r.updated}${r.errors.length ? `; błędy: ${r.errors.length}` : ""}`);
      qc.invalidateQueries({ queryKey: ["mailing-contacts"] });
      qc.invalidateQueries({ queryKey: ["mailing-contacts-stats"] });
    },
    onError: (e) => setMsg(errMsg(e)),
  });

  const importFulM = useMutation({
    mutationFn: () => api.mailingImportFulfillment("buyer"),
    onSuccess: (r) => {
      setMsg(`Z zamówień: +${r.created} / upd ${r.updated}`);
      qc.invalidateQueries({ queryKey: ["mailing-contacts"] });
      qc.invalidateQueries({ queryKey: ["mailing-contacts-stats"] });
    },
    onError: (e) => setMsg(errMsg(e)),
  });

  const stats = statsQ.data;
  const toggleStatus = (status: string) => {
    setTagFilter("");
    setStatusFilter((prev) => (prev === status ? "" : status));
  };
  const toggleTag = (tag: string) => {
    setStatusFilter("");
    setTagFilter((prev) => (prev === tag ? "" : tag));
  };
  const clearFilters = () => {
    setStatusFilter("");
    setTagFilter("");
  };

  type Tile = {
    key: string;
    label: string;
    count: number | undefined;
    active: boolean;
    onClick: () => void;
  };

  const tiles: Tile[] = [
    {
      key: "total",
      label: "Razem",
      count: stats?.total,
      active: !statusFilter && !tagFilter,
      onClick: clearFilters,
    },
    {
      key: "active",
      label: "Aktywni",
      count: stats?.by_status?.active,
      active: statusFilter === "active",
      onClick: () => toggleStatus("active"),
    },
    {
      key: "unsubscribed",
      label: "Wypisani",
      count: stats?.by_status?.unsubscribed,
      active: statusFilter === "unsubscribed",
      onClick: () => toggleStatus("unsubscribed"),
    },
    {
      key: "bounced",
      label: "Bounce",
      count: stats?.by_status?.bounced,
      active: statusFilter === "bounced",
      onClick: () => toggleStatus("bounced"),
    },
    {
      key: "complained",
      label: "Complaint",
      count: stats?.by_status?.complained,
      active: statusFilter === "complained",
      onClick: () => toggleStatus("complained"),
    },
    {
      key: "suppressed",
      label: "Suppressed",
      count: stats?.suppressed,
      active: false,
      onClick: () => {},
    },
    {
      key: "buyer",
      label: "Kupili",
      count: stats?.by_tag?.buyer,
      active: tagFilter === "buyer",
      onClick: () => toggleTag("buyer"),
    },
    {
      key: "mailerlite",
      label: "MailerLite",
      count: stats?.by_tag?.mailerlite,
      active: tagFilter === "mailerlite",
      onClick: () => toggleTag("mailerlite"),
    },
    {
      key: "ml_never_opened",
      label: "Nigdy nie otworzyli (ML)",
      count: stats?.by_tag?.ml_never_opened,
      active: tagFilter === "ml_never_opened",
      onClick: () => toggleTag("ml_never_opened"),
    },
    {
      key: "ml_opened",
      label: "Otwierali (ML)",
      count: stats?.by_tag?.ml_opened,
      active: tagFilter === "ml_opened",
      onClick: () => toggleTag("ml_opened"),
    },
    {
      key: "rs_opened",
      label: "Otwarli (Resend)",
      count: stats?.by_tag?.rs_opened,
      active: tagFilter === "rs_opened",
      onClick: () => toggleTag("rs_opened"),
    },
    {
      key: "rs_clicked",
      label: "Kliknęli (Resend)",
      count: stats?.by_tag?.rs_clicked,
      active: tagFilter === "rs_clicked",
      onClick: () => toggleTag("rs_clicked"),
    },
    {
      key: "waitlist",
      label: "Waitlista",
      count: stats?.by_tag?.waitlist,
      active: tagFilter === "waitlist",
      onClick: () => toggleTag("waitlist"),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {tiles.map((t) => {
          const clickable = t.key !== "suppressed";
          return (
            <button
              key={t.key}
              type="button"
              disabled={!clickable}
              onClick={clickable ? t.onClick : undefined}
              className={cn(
                "min-w-[7.5rem] rounded-lg border px-3 py-2 text-left transition-colors",
                t.active
                  ? "border-primary bg-primary/10"
                  : "border-border/70 bg-background",
                clickable ? "hover:border-primary/60 cursor-pointer" : "cursor-default opacity-90",
              )}
              title={
                t.key === "suppressed"
                  ? "Tylko licznik (lista filtruje po statusie / tagu)"
                  : t.active
                    ? "Kliknij, żeby wyczyścić filtr"
                    : "Kliknij, żeby filtrować listę"
              }
            >
              <div className="text-[11px] text-muted-foreground leading-tight">{t.label}</div>
              <div className="text-lg font-semibold tabular-nums">
                {t.count === undefined ? "…" : t.count}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        ML = import MailerLite. Resend = otwarcia/kliki z naszych wysyłek (wymaga tracking subdomain).
        {(statusFilter || tagFilter) && (
          <>
            {" "}
            Filtr: {statusFilter ? `status=${statusFilter}` : `tag=${tagFilter}`} · lista:{" "}
            {listQ.data?.total ?? "…"}
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Szukaj email / imię"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
        <Button type="button" variant="outline" onClick={() => importFulM.mutate()} disabled={importFulM.isPending}>
          Import z zamówień
        </Button>
      </div>
      <div className="grid gap-2 rounded-lg border border-border/70 p-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input placeholder="email@…" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input placeholder="Imię (opcjonalnie)" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="button" onClick={() => createM.mutate()} disabled={!email || createM.isPending}>
          Dodaj
        </Button>
      </div>
      <div className="rounded-lg border border-border/70 p-3 space-y-2">
        <div className="text-sm font-medium">Import CSV (kolumna email)</div>
        <textarea
          className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          placeholder={"email,name\na@b.pl,Ania"}
        />
        <Button type="button" variant="secondary" onClick={() => importCsvM.mutate()} disabled={!csv || importCsvM.isPending}>
          Importuj CSV
        </Button>
      </div>
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
      <ul className="divide-y divide-border/60 rounded-lg border border-border/70">
        {(listQ.data?.items || []).map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
            <div>
              <div className="font-medium">{c.email_normalized}</div>
              <div className="text-xs text-muted-foreground">
                {c.name || "-"} · {c.status}
                {c.tags?.length ? ` · ${c.tags.join(", ")}` : ""}
                {c.suppressed ? " · suppressed" : ""}
              </div>
            </div>
          </li>
        ))}
        {!listQ.isLoading && !(listQ.data?.items || []).length && (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">Brak kontaktów.</li>
        )}
      </ul>
    </div>
  );
}

function TemplatesTab() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState("");
  const [edit, setEdit] = useState<MailingTemplate | null>(null);
  const [editorKey, setEditorKey] = useState("new");

  const listQ = useQuery({
    queryKey: ["mailing-templates"],
    queryFn: () => api.mailingTemplates(),
  });

  const resetForm = useCallback(() => {
    setEdit(null);
    setName("");
    setSubject("");
    setBody("");
    setPreview("");
    setEditorKey(`new-${Date.now()}`);
  }, []);

  const saveM = useMutation({
    mutationFn: async () => {
      if (edit) {
        return api.mailingPatchTemplate(edit.id, { name, subject, body_md: body });
      }
      return api.mailingCreateTemplate({ name, subject, body_md: body });
    },
    onSuccess: () => {
      resetForm();
      qc.invalidateQueries({ queryKey: ["mailing-templates"] });
    },
  });

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="space-y-2">
        <Input placeholder="Nazwa szablonu" value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder="Temat" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <MergeChips onInsert={(tok) => setSubject((s) => s + tok)} />
        <MailingEditor editorKey={editorKey} value={body} onChange={setBody} />
        <MergeChips onInsert={(tok) => setBody((b) => `${b}${b.endsWith("\n") || !b ? "" : "\n"}${tok}`)} />
        <p className="text-[11px] text-muted-foreground">
          Stopka z Ustawień dokleja się automatycznie przy wysyłce i w podglądzie.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={async () => {
              const r = await api.mailingPreview(body, true);
              setPreview(r.html);
            }}
          >
            Podgląd (+ stopka)
          </Button>
          <Button type="button" onClick={() => saveM.mutate()} disabled={!name || saveM.isPending}>
            {edit ? "Zapisz" : "Utwórz"}
          </Button>
          {edit && (
            <Button type="button" variant="ghost" onClick={resetForm}>
              Anuluj edycję
            </Button>
          )}
        </div>
        {preview && (
          <div
            className="prose prose-sm max-w-none rounded-md border border-border/70 bg-background/50 p-3"
            dangerouslySetInnerHTML={{ __html: preview }}
          />
        )}
      </div>
      <ul className="divide-y divide-border/60 rounded-lg border border-border/70 self-start">
        {(listQ.data?.items || []).map((t) => (
          <li key={t.id} className="flex items-start justify-between gap-2 px-3 py-2 text-sm">
            <button
              type="button"
              className="text-left"
              onClick={() => {
                setEdit(t);
                setName(t.name);
                setSubject(t.subject);
                setBody(t.body_md);
                setEditorKey(t.id);
                setPreview("");
              }}
            >
              <div className="font-medium">{t.name}</div>
              <div className="text-xs text-muted-foreground">{t.subject}</div>
            </button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() =>
                api.mailingDeleteTemplate(t.id).then(() =>
                  qc.invalidateQueries({ queryKey: ["mailing-templates"] }),
                )
              }
            >
              Usuń
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CampaignsTab() {
  const qc = useQueryClient();
  const [view, setView] = useState<"list" | "edit" | "track">("list");
  const [editId, setEditId] = useState<string | null>(null);
  const [trackId, setTrackId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState("");
  const [audienceMode, setAudienceMode] = useState<"all_active" | "tags">("all_active");
  const [tag, setTag] = useState("buyer");
  const [msg, setMsg] = useState("");
  const [preview, setPreview] = useState("");
  const [editorKey, setEditorKey] = useState("camp-new");
  const [testEmail, setTestEmail] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [sendStatusFilter, setSendStatusFilter] = useState("");

  const listQ = useQuery({
    queryKey: ["mailing-campaigns"],
    queryFn: () => api.mailingCampaigns(),
    refetchInterval: 15_000,
  });

  const templatesQ = useQuery({
    queryKey: ["mailing-templates"],
    queryFn: () => api.mailingTemplates(),
  });

  const trackQ = useQuery({
    queryKey: ["mailing-campaign", trackId],
    queryFn: () => api.mailingGetCampaign(trackId!),
    enabled: view === "track" && !!trackId,
    refetchInterval: (q) => {
      const st = q.state.data?.status;
      return st === "queued" || st === "sending" ? 10_000 : 30_000;
    },
  });

  const sendsQ = useQuery({
    queryKey: ["mailing-campaign-sends", trackId, sendStatusFilter],
    queryFn: () =>
      api.mailingCampaignSends(trackId!, {
        status: sendStatusFilter || undefined,
        limit: 100,
      }),
    enabled: view === "track" && !!trackId,
    refetchInterval: () => {
      const st = trackQ.data?.status;
      return st === "queued" || st === "sending" ? 10_000 : false;
    },
  });

  const resetForm = useCallback(() => {
    setEditId(null);
    setName("");
    setSubject("");
    setBody("");
    setAudienceMode("all_active");
    setTag("buyer");
    setPreview("");
    setTemplateId("");
    setEditorKey(`camp-${Date.now()}`);
  }, []);

  const openNew = () => {
    resetForm();
    setView("edit");
    setMsg("");
  };

  const openEdit = async (c: MailingCampaign) => {
    if (c.status === "draft" || c.status === "cancelled") {
      try {
        const full = await api.mailingGetCampaign(c.id);
        setEditId(full.id);
        setName(full.name || "");
        setSubject(full.subject || "");
        setBody(full.body_md || "");
        const aud = full.audience || { mode: "all_active", tags: [] };
        if (aud.mode === "tags" && aud.tags?.length) {
          setAudienceMode("tags");
          setTag(aud.tags[0] || "buyer");
        } else {
          setAudienceMode("all_active");
        }
        setPreview("");
        setEditorKey(full.id);
        setView("edit");
        setMsg(`Edycja: ${full.status}`);
      } catch (e) {
        setMsg(errMsg(e));
      }
      return;
    }
    setTrackId(c.id);
    setSendStatusFilter("");
    setView("track");
    setMsg("");
  };

  const saveM = useMutation({
    mutationFn: async () => {
      const audience =
        audienceMode === "tags"
          ? { mode: "tags" as const, tags: [tag] }
          : { mode: "all_active" as const, tags: [] as string[] };
      if (editId) {
        return api.mailingPatchCampaign(editId, {
          name: name || subject.slice(0, 60),
          subject,
          body_md: body,
          audience,
        });
      }
      return api.mailingCreateCampaign({
        name: name || subject.slice(0, 60),
        subject,
        body_md: body,
        audience,
      });
    },
    onSuccess: (c) => {
      setMsg(editId ? "Zapisano draft." : "Utworzono draft.");
      setEditId(c.id);
      qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
    },
    onError: (e) => setMsg(errMsg(e)),
  });

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const t = (templatesQ.data?.items || []).find((x) => x.id === id);
    if (!t) return;
    setSubject(t.subject || "");
    setBody(t.body_md || "");
    if (!name.trim()) setName(t.name || "");
    setEditorKey(`tpl-${t.id}-${Date.now()}`);
    setMsg(`Wstawiono szablon: ${t.name}`);
  };

  if (view === "track" && trackId) {
    const camp = trackQ.data;
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setView("list");
              setTrackId(null);
              qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
            }}
          >
            ← Lista
          </Button>
          {camp && ["queued", "sending", "draft"].includes(camp.status) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy === camp.id}
              onClick={async () => {
                setBusy(camp.id);
                try {
                  await api.mailingCancelCampaign(camp.id);
                  qc.invalidateQueries({ queryKey: ["mailing-campaign", trackId] });
                  qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
                } finally {
                  setBusy(null);
                }
              }}
            >
              Anuluj wysyłkę
            </Button>
          )}
        </div>
        {trackQ.isLoading && <p className="text-sm text-muted-foreground">Ładowanie…</p>}
        {camp && (
          <div className="space-y-3 rounded-lg border border-border/70 p-3">
            <div>
              <div className="text-base font-semibold">{camp.name || camp.subject}</div>
              <div className="text-sm text-muted-foreground">{camp.subject}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                Status: <span className="font-medium text-foreground">{camp.status}</span>
                {camp.audience_count != null ? ` · odbiorców (audience): ${camp.audience_count}` : ""}
                {camp.queued_at ? ` · kolejka: ${camp.queued_at}` : ""}
                {camp.finished_at ? ` · koniec: ${camp.finished_at}` : ""}
              </div>
            </div>
            <ProgressBar progress={camp.progress} />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              {(
                [
                  ["Wysłane", camp.engagement?.sent],
                  ["Dostarczone", camp.engagement?.delivered],
                  ["Otwarte", camp.engagement?.opened],
                  ["Kliknięte", camp.engagement?.clicked],
                  ["Zakupy", camp.engagement?.purchased],
                  [
                    "Przychód",
                    camp.engagement?.revenue != null
                      ? `${Number(camp.engagement.revenue).toFixed(0)} zł`
                      : undefined,
                  ],
                ] as const
              ).map(([label, val]) => (
                <div
                  key={label}
                  className="rounded-md border border-border/70 bg-background/60 px-2 py-1.5"
                >
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                  <div className="text-sm font-semibold tabular-nums">
                    {val === undefined ? "…" : val}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Otwarcia z Resend (Apple MPP może zawyżać). Zakupy: last-touch po emailu (klik ≤7 dni, inaczej
              wysyłka ≤14 dni).
            </p>
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(camp.stats || {}).map(([k, v]) => (
                <span key={k} className="rounded-md border border-border px-2 py-0.5">
                  {k}: {v}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-muted-foreground">Filtr statusu</label>
              <select
                className="rounded-md border border-input bg-background px-2 py-1 text-sm"
                value={sendStatusFilter}
                onChange={(e) => setSendStatusFilter(e.target.value)}
              >
                <option value="">wszystkie</option>
                {[
                  "pending",
                  "sent",
                  "delivered",
                  "opened",
                  "clicked",
                  "failed",
                  "skipped",
                  "bounced",
                  "complained",
                ].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <span className="text-xs text-muted-foreground">
                {sendsQ.data?.total ?? "…"} wierszy
              </span>
            </div>
            <CampaignSendsTable items={sendsQ.data?.items || []} loading={sendsQ.isLoading} />
          </div>
        )}
      </div>
    );
  }

  if (view === "edit") {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setView("list");
              resetForm();
            }}
          >
            ← Lista
          </Button>
          <span className="text-sm text-muted-foreground">
            {editId ? "Edycja draftu" : "Nowy draft"}
          </span>
        </div>
        <div className="space-y-2 rounded-lg border border-border/70 p-3">
          <Input
            placeholder="Nazwa (opcjonalnie)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input placeholder="Temat" value={subject} onChange={(e) => setSubject(e.target.value)} />
          <MergeChips onInsert={(tok) => setSubject((s) => s + tok)} />
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <label className="text-xs text-muted-foreground">Szablon</label>
            <select
              className="max-w-xs rounded-md border border-input bg-background px-2 py-1 text-sm"
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
            >
              <option value="">Wstaw szablon…</option>
              {(templatesQ.data?.items || []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <MailingEditor editorKey={editorKey} value={body} onChange={setBody} />
          <MergeChips
            onInsert={(tok) => setBody((b) => `${b}${b.endsWith("\n") || !b ? "" : "\n"}${tok}`)}
          />
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={audienceMode === "all_active"}
                onChange={() => setAudienceMode("all_active")}
              />
              Wszyscy aktywni
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                checked={audienceMode === "tags"}
                onChange={() => setAudienceMode("tags")}
              />
              Tag
            </label>
            {audienceMode === "tags" && (
              <Input className="max-w-[140px]" value={tag} onChange={(e) => setTag(e.target.value)} />
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const r = await api.mailingPreview(body, true);
                setPreview(r.html);
              }}
            >
              Podgląd (+ stopka)
            </Button>
            <Button
              type="button"
              onClick={() => saveM.mutate()}
              disabled={!subject || !body || saveM.isPending}
            >
              {editId ? "Zapisz" : "Utwórz draft"}
            </Button>
            {editId && (
              <Button
                type="button"
                disabled={busy === editId || !subject || !body}
                onClick={async () => {
                  setBusy(editId);
                  try {
                    await saveM.mutateAsync();
                    await api.mailingSendCampaign(editId);
                    setMsg("W kolejce.");
                    setTrackId(editId);
                    setView("track");
                    resetForm();
                    qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
                  } catch (e) {
                    setMsg(errMsg(e));
                  } finally {
                    setBusy(null);
                  }
                }}
              >
                Wyślij
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-center border-t border-border/50 pt-2">
            <Input
              className="max-w-xs"
              placeholder="test@email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              disabled={!testEmail || !subject || !body}
              onClick={async () => {
                try {
                  const r = await api.mailingTestSend({
                    to_email: testEmail,
                    subject,
                    body_md: body,
                  });
                  setMsg(`Test: ${r.to} (id ${r.resend_id || "?"})`);
                } catch (e) {
                  setMsg(errMsg(e));
                }
              }}
            >
              Wyślij test z tej treści
            </Button>
          </div>
          {preview && (
            <div
              className="prose prose-sm max-w-none rounded-md border border-border/70 bg-background/50 p-3"
              dangerouslySetInnerHTML={{ __html: preview }}
            />
          )}
        </div>
        {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={openNew}>
          Nowy draft
        </Button>
      </div>
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
      <CampaignList
        items={listQ.data?.items || []}
        busy={busy}
        onOpen={openEdit}
        onSend={async (c) => {
          setBusy(c.id);
          try {
            await api.mailingSendCampaign(c.id);
            setTrackId(c.id);
            setView("track");
            qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
          } catch (e) {
            setMsg(errMsg(e));
          } finally {
            setBusy(null);
          }
        }}
        onCancel={async (c) => {
          setBusy(c.id);
          try {
            await api.mailingCancelCampaign(c.id);
            qc.invalidateQueries({ queryKey: ["mailing-campaigns"] });
          } finally {
            setBusy(null);
          }
        }}
      />
    </div>
  );
}

function CampaignSendsTable({
  items,
  loading,
}: {
  items: MailingCampaignSend[];
  loading?: boolean;
}) {
  if (loading && !items.length) {
    return <p className="text-sm text-muted-foreground">Ładowanie wysyłek…</p>;
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border/60">
      <table className="w-full text-left text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 font-medium">Email</th>
            <th className="px-2 py-1.5 font-medium">Imię</th>
            <th className="px-2 py-1.5 font-medium">Status</th>
            <th className="px-2 py-1.5 font-medium">Klik</th>
            <th className="px-2 py-1.5 font-medium">Błąd</th>
            <th className="px-2 py-1.5 font-medium">Aktualizacja</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/50">
          {items.map((s) => (
            <tr key={s.id}>
              <td className="px-2 py-1.5 font-mono">{s.email_normalized}</td>
              <td className="px-2 py-1.5">{s.name || "-"}</td>
              <td className="px-2 py-1.5">{s.status}</td>
              <td
                className="max-w-[10rem] truncate px-2 py-1.5 text-muted-foreground"
                title={s.click_url || ""}
              >
                {s.clicked_at ? s.click_url || "tak" : s.opened_at ? "otwarte" : ""}
              </td>
              <td className="max-w-[12rem] truncate px-2 py-1.5 text-red-700" title={s.error || ""}>
                {s.error || ""}
              </td>
              <td className="px-2 py-1.5 text-muted-foreground">{s.updated_at || ""}</td>
            </tr>
          ))}
          {!items.length && (
            <tr>
              <td colSpan={6} className="px-2 py-6 text-center text-muted-foreground">
                Brak wierszy wysyłki (jeszcze nie kolejkano albo pusty filtr).
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function CampaignList({
  items,
  busy,
  onOpen,
  onSend,
  onCancel,
}: {
  items: MailingCampaign[];
  busy: string | null;
  onOpen: (c: MailingCampaign) => void;
  onSend: (c: MailingCampaign) => void;
  onCancel: (c: MailingCampaign) => void;
}) {
  return (
    <ul className="divide-y divide-border/60 rounded-lg border border-border/70">
      {items.map((c) => (
        <li key={c.id} className="flex flex-wrap items-start justify-between gap-2 px-3 py-3 text-sm">
          <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onOpen(c)}>
            <div className="font-medium hover:text-brand">{c.name || c.subject}</div>
            <div className="text-xs text-muted-foreground">
              {c.status}
              {c.progress && c.progress.total
                ? ` · ${c.progress.done}/${c.progress.total} (${c.progress.pct}%)`
                : ""}
              {c.engagement && (c.engagement.opened || c.engagement.clicked || c.engagement.purchased)
                ? ` · o${c.engagement.opened}/k${c.engagement.clicked}/z${c.engagement.purchased}`
                : ""}
            </div>
            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{c.subject}</div>
            <ProgressBar progress={c.progress} className="mt-2 max-w-xs" />
          </button>
          <div className="flex gap-2">
            {(c.status === "draft" || c.status === "cancelled") && (
              <Button
                type="button"
                size="sm"
                disabled={busy === c.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onSend(c);
                }}
              >
                Wyślij
              </Button>
            )}
            {["queued", "sending", "draft"].includes(c.status) && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy === c.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(c);
                }}
              >
                Anuluj
              </Button>
            )}
          </div>
        </li>
      ))}
      {!items.length && (
        <li className="px-3 py-6 text-center text-sm text-muted-foreground">Brak kampanii.</li>
      )}
    </ul>
  );
}

function SequencesTab() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<MailingSequence | null>(null);
  const [steps, setSteps] = useState<MailingSequenceStep[]>([]);
  const [enrollTag, setEnrollTag] = useState("buyer");
  const [msg, setMsg] = useState("");
  const [stepsKey, setStepsKey] = useState(0);

  const listQ = useQuery({
    queryKey: ["mailing-sequences"],
    queryFn: () => api.mailingSequences(),
  });

  const createM = useMutation({
    mutationFn: () => api.mailingCreateSequence({ name }),
    onSuccess: (s) => {
      setName("");
      setSelected(s);
      setSteps(
        (s.steps || []).length
          ? s.steps
          : [{ delay_hours: 0, subject: "", body_md: "" }],
      );
      setStepsKey((k) => k + 1);
      qc.invalidateQueries({ queryKey: ["mailing-sequences"] });
    },
  });

  const updateStep = (idx: number, patch: Partial<MailingSequenceStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Input
          className="max-w-xs"
          placeholder="Nazwa serii, np. Welcome po zakupie"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="button" onClick={() => createM.mutate()} disabled={!name || createM.isPending}>
          Nowa seria
        </Button>
      </div>
      <ul className="flex flex-wrap gap-2">
        {(listQ.data?.items || []).map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setSelected(s);
              setSteps(
                (s.steps || []).length
                  ? s.steps.map((st) => ({
                      delay_hours: st.delay_hours ?? 0,
                      subject: st.subject || "",
                      body_md: st.body_md || "",
                    }))
                  : [{ delay_hours: 0, subject: "", body_md: "" }],
              );
              setStepsKey((k) => k + 1);
            }}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm",
              selected?.id === s.id ? "border-brand bg-brand/10" : "border-border",
            )}
          >
            {s.name} ({s.steps?.length || 0} listów, {s.enrollment_count || 0} osób)
          </button>
        ))}
      </ul>
      {!selected && (
        <p className="rounded-md border border-dashed border-border px-3 py-8 text-center text-sm text-muted-foreground">
          Utwórz serię albo kliknij istniejącą. To łańcuch listów, nie jeden strzał.
        </p>
      )}
      {selected && (
        <div className="space-y-3 rounded-lg border border-border/70 p-3">
          <div className="text-sm font-medium">{selected.name}</div>
          <p className="text-xs text-muted-foreground">
            Każdy krok to osobny list. Zapisz kroki, zanim włączysz osoby.
          </p>
          {steps.map((st, idx) => (
            <div key={`${stepsKey}-${idx}`} className="space-y-2 rounded-md border border-border/50 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Krok {idx + 1}</span>
                <Input
                  className="max-w-[100px]"
                  type="number"
                  min={0}
                  value={st.delay_hours}
                  onChange={(e) => updateStep(idx, { delay_hours: Number(e.target.value) || 0 })}
                  title="Opóźnienie (h)"
                />
                <span className="text-xs text-muted-foreground">
                  {idx === 0 ? "h od włączenia (0 = od razu)" : "h po poprzednim"}
                </span>
                <Input
                  className="min-w-[160px] flex-1"
                  placeholder="Temat"
                  value={st.subject}
                  onChange={(e) => updateStep(idx, { subject: e.target.value })}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={steps.length <= 1}
                  onClick={() => setSteps((prev) => prev.filter((_, i) => i !== idx))}
                >
                  Usuń
                </Button>
              </div>
              <MergeChips
                onInsert={(tok) => updateStep(idx, { subject: `${st.subject}${tok}` })}
              />
              <MailingEditor
                editorKey={`${selected.id}-step-${stepsKey}-${idx}`}
                value={st.body_md}
                onChange={(md) => updateStep(idx, { body_md: md })}
                minHeightClass="min-h-[140px]"
              />
              <MergeChips
                onInsert={(tok) =>
                  updateStep(idx, {
                    body_md: `${st.body_md}${st.body_md.endsWith("\n") || !st.body_md ? "" : "\n"}${tok}`,
                  })
                }
              />
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setSteps((prev) => [...prev, { delay_hours: 48, subject: "", body_md: "" }])
              }
            >
              Dodaj krok
            </Button>
            <Button
              type="button"
              onClick={async () => {
                try {
                  const s = await api.mailingPutSequenceSteps(selected.id, steps);
                  setSelected(s);
                  setMsg("Zapisano kroki.");
                  qc.invalidateQueries({ queryKey: ["mailing-sequences"] });
                } catch (e) {
                  setMsg(errMsg(e));
                }
              }}
            >
              Zapisz kroki
            </Button>
            <Input
              className="max-w-[140px]"
              value={enrollTag}
              onChange={(e) => setEnrollTag(e.target.value)}
              placeholder="tag, np. buyer"
            />
            <Button
              type="button"
              variant="secondary"
              onClick={async () => {
                try {
                  const r = await api.mailingEnrollSequence(selected.id, { tag: enrollTag });
                  setMsg(`Enroll: +${r.enrolled}, pominięte ${r.skipped}`);
                  qc.invalidateQueries({ queryKey: ["mailing-sequences"] });
                } catch (e) {
                  setMsg(errMsg(e));
                }
              }}
            >
              Włącz kontakty z tym tagiem
            </Button>
          </div>
        </div>
      )}
      {msg && <p className="text-sm text-muted-foreground">{msg}</p>}
    </div>
  );
}

function SettingsTab() {
  const qc = useQueryClient();
  const [testEmail, setTestEmail] = useState("");
  const [rate, setRate] = useState("40");
  const [footer, setFooter] = useState("");
  const [footerKey, setFooterKey] = useState("footer");
  const [footerLoaded, setFooterLoaded] = useState(false);
  const [msg, setMsg] = useState("");

  const settingsQ = useQuery({
    queryKey: ["mailing-settings"],
    queryFn: () => api.mailingSettings(),
  });

  const s = settingsQ.data;

  useEffect(() => {
    if (!s || footerLoaded) return;
    setFooter(s.footer_md || "");
    setFooterKey(`footer-${Date.now()}`);
    setRate(String(s.rate_per_minute || 40));
    setFooterLoaded(true);
  }, [s, footerLoaded]);

  return (
    <div className="space-y-4 text-sm">
      <ResendUsageStrip detailed />
      <div className="rounded-lg border border-border/70 p-3 space-y-1">
        <div>API key: {s?.api_key_configured ? "OK" : "brak"}</div>
        <div>
          From: {s?.from_name || "-"} &lt;{s?.from_email || "-"}&gt;{" "}
          {s?.from_ready ? "(ready)" : "(niegotowe)"}
        </div>
        <div>Webhook secret: {s?.webhook_secret_configured ? "OK" : "brak"}</div>
        <div>Public base: {s?.public_base_url}</div>
        <div>Rate: {s?.rate_per_minute}/min</div>
        {s?.test_only && (
          <div className="text-amber-800">
            Test allowlist: {(s.test_allowlist || []).join(", ") || "(pusta)"}
          </div>
        )}
      </div>
      <div className="space-y-2 rounded-lg border border-border/70 p-3">
        <div className="font-medium">Stopka (doklejana do każdego maila)</div>
        <p className="text-xs text-muted-foreground">
          Pod treścią kampanii/szablonu/sekwencji. Link wypisu dokleja się osobno na końcu.
        </p>
        {footerLoaded && (
          <MailingEditor
            editorKey={footerKey}
            value={footer}
            onChange={setFooter}
            minHeightClass="min-h-[120px]"
          />
        )}
        <Button
          type="button"
          onClick={async () => {
            try {
              await api.mailingSetFooter(footer);
              setMsg("Zapisano stopkę.");
              qc.invalidateQueries({ queryKey: ["mailing-settings"] });
            } catch (e) {
              setMsg(errMsg(e));
            }
          }}
        >
          Zapisz stopkę
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          className="max-w-[100px]"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          placeholder="rate"
        />
        <Button
          type="button"
          variant="outline"
          onClick={async () => {
            try {
              await api.mailingSetRate(Number(rate) || 40);
              setMsg("Zapisano rate.");
              qc.invalidateQueries({ queryKey: ["mailing-settings"] });
            } catch (e) {
              setMsg(errMsg(e));
            }
          }}
        >
          Ustaw rate/min
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          className="max-w-xs"
          placeholder="test@email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
        />
        <Button
          type="button"
          onClick={async () => {
            try {
              const r = await api.mailingTestSend({
                to_email: testEmail,
                subject: "Test z panelu Mailing",
                body_md: "To jest **test** z `/mailing`.",
              });
              setMsg(`Wysłano test: ${r.to} (id ${r.resend_id || "?"})`);
            } catch (e) {
              setMsg(errMsg(e));
            }
          }}
          disabled={!testEmail}
        >
          Wyślij test
        </Button>
      </div>
      {msg && <p className="text-muted-foreground">{msg}</p>}
      {(s?.recent_webhooks || []).length > 0 && (
        <div>
          <div className="mb-1 font-medium">Ostatnie webhooki</div>
          <ul className="text-xs text-muted-foreground space-y-1">
            {(s?.recent_webhooks || []).map((w) => (
              <li key={String(w.id)}>
                {String(w.event_type)} · {String(w.resend_id)} · {String(w.created_at)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
